<script context="module">
  import {
    postFile,
    postJsonFile,
    getDataOutside,
    api,
    apiOutside,
  } from "../../utilities/Utils.svelte";
  import { BASE_URL } from "../../utilities/config";
  import { AzureBlobStorageService } from "../../utilities/azureBlobStorage.js";

  export function uploadMediaFileName(instanceId, exerciseId) {
    return "AS_" + instanceId + "_" + exerciseId + ".ogg";
  }

  export function uploadMediaFileUrl(instanceId, exerciseId, fileName = uploadMediaFileName(instanceId, exerciseId)) {
    const azureService = new AzureBlobStorageService();
    // For Azure, the public URL would be:
    return `${azureService.containerUrl}/recordings/${fileName}`;
  }

  async function uploadToGoogle(fileName, dataBlob) {
    let fileParams = {
      uploadType: "media",
      name: "recordings/" + fileName,
      key: "AIzaSyBgFT_wbOWOauPZpCWoBiRVGmgFdfHvr6o",
    };

    return await apiOutside
      .post(
        "https://storage.googleapis.com/upload/storage/v1/b/sensay-assessment/o",
        {
          headers: {
            "Content-Type": "audio/ogg",
          },
          searchParams: fileParams,
          body: dataBlob,
        },
      )
      .json();
  }

  async function uploadToAzure(fileName, dataBlob) {
    const azureService = new AzureBlobStorageService();
    
    // Upload to Azure Blob Storage in recordings folder
    const uploadUrl = `${azureService.containerUrl}/recordings/${fileName}?${azureService.sasToken}`;
    
    const response = await fetch(uploadUrl, {
      method: 'PUT',
      headers: {
        'x-ms-blob-type': 'BlockBlob',
        'Content-Type': 'audio/ogg',
      },
      body: dataBlob,
    });

    if (!response.ok) {
      throw new Error(`Azure upload failed: ${response.status} ${response.statusText}`);
    }

    return {
      fileName,
      url: uploadUrl.split('?')[0], // Remove SAS token for public URL
    };
  }

  // Media upload uses a raw fetch (Azure) / single POST (Google), neither of
  // which retries on its own. A transient network blip on the recording upload
  // therefore failed the whole submit. Retry with a small backoff so the audio
  // matches the resilience the results upload already gets from ky.
  const MEDIA_UPLOAD_MAX_ATTEMPTS = 3;
  const MEDIA_UPLOAD_RETRY_BASE_MS = 600;

  const delay = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

  // `fileName` is given for the sentences of a LISTEN_REPEAT series, which
  // each have their own recording.
  export async function uploadMedia(
    instanceId,
    exerciseId,
    dataBlob,
    storageProvider = "azure",
    fileName = uploadMediaFileName(instanceId, exerciseId),
  ) {

    let lastError;
    for (let attempt = 1; attempt <= MEDIA_UPLOAD_MAX_ATTEMPTS; attempt++) {
      try {
        let uploadResult;

        if (storageProvider === "azure") {
          console.log(`Uploading audio to Azure Blob Storage (attempt ${attempt}/${MEDIA_UPLOAD_MAX_ATTEMPTS})...`);
          uploadResult = await uploadToAzure(fileName, dataBlob);
        } else {
          console.log(`Uploading audio to Google Cloud Storage (attempt ${attempt}/${MEDIA_UPLOAD_MAX_ATTEMPTS})...`);
          uploadResult = await uploadToGoogle(fileName, dataBlob);
        }

        console.log(`Audio upload successful (${storageProvider}):`, uploadResult);
        return;
      } catch (error) {
        lastError = error;
        console.error("Error uploading audio recording:", {
          error: error?.message || error,
          storageProvider,
          attempt,
          maxAttempts: MEDIA_UPLOAD_MAX_ATTEMPTS,
          instanceId,
          exerciseId,
          fileName,
          blobSize: dataBlob?.size,
          blobType: dataBlob?.type,
          userAgent: navigator.userAgent,
          timestamp: new Date().toISOString(),
        });

        if (attempt < MEDIA_UPLOAD_MAX_ATTEMPTS) {
          await delay(MEDIA_UPLOAD_RETRY_BASE_MS * attempt);
        }
      }
    }

    // Every attempt failed. Report once to TrackJS and re-throw so the caller
    // keeps the recording, stays on the exercise, and shows the retry UI.
    if (window.TrackJS) {
      try {
        window.TrackJS.track(
          lastError instanceof Error
            ? lastError
            : new Error(`uploadMedia failed after ${MEDIA_UPLOAD_MAX_ATTEMPTS} attempts: ${lastError}`),
        );
      } catch (_) {
        /* never let logging break the flow */
      }
    }
    throw lastError;
  }

  function prepareExerciseResultData(
    instanceId,
    exerciseId,
    aggregateScore,
    inputData,
    recordingFileName = uploadMediaFileName(instanceId, exerciseId),
  ) {
    return {
      data: JSON.stringify(aggregateScore),
      rawData: JSON.stringify(inputData),
      recordingFileName: recordingFileName,
      recordingFileUrl: uploadMediaFileUrl(instanceId, exerciseId, recordingFileName),
      overallScore: Number.parseFloat(aggregateScore.pronScore),
    };
  }

  export async function uploadExerciseResults(
    instanceId,
    exerciseId,
    aggregateScore,
    exerciseResultData,
    recordingFileName = undefined,
  ) {
    const startTime = Date.now();
    console.log("[SCORING-DIAG] uploadExerciseResults called", {
      instanceId,
      exerciseId,
      hasAggregateScore: !!aggregateScore,
      pronScore: aggregateScore?.pronScore,
      hasExerciseResultData: !!exerciseResultData,
      exerciseResultDataLength: exerciseResultData?.length,
      timestamp: new Date().toISOString(),
    });

    const exerciseResultDataJson = prepareExerciseResultData(
      instanceId,
      exerciseId,
      aggregateScore,
      exerciseResultData,
      recordingFileName,
    );

    try {
      // throwHttpErrors:true overrides the global `api` setting so a non-2xx
      // response (after ky's internal retries) rejects instead of being treated
      // as success. The caller MUST be able to detect a failed save so it does
      // not advance to the next exercise on data loss.
      const data = await api
        .post(
          "public/assessment/instance/" + instanceId + "/result/" + exerciseId,
          {
            headers: {
              "Content-Type": "application/json",
            },
            json: exerciseResultDataJson,
            throwHttpErrors: true,
          },
        )
        .json();

      console.log("[SCORING-DIAG] uploadExerciseResults SUCCESS", {
        instanceId,
        exerciseId,
        responseData: data,
        elapsedMs: Date.now() - startTime,
      });

      return data;
    } catch (error) {
      console.error("[SCORING-DIAG] uploadExerciseResults FAILED", {
        instanceId,
        exerciseId,
        error: error?.message || error,
        errorName: error?.name,
        status: error?.response?.status,
        elapsedMs: Date.now() - startTime,
        timestamp: new Date().toISOString(),
      });
      // Re-throw so the caller can keep the user on the exercise and retry
      // instead of silently skipping it.
      throw error;
    }
  }

  export async function submitFinalAssessmentResults(instanceId) {
    const startTime = Date.now();
    console.log("[SCORING-DIAG] submitFinalAssessmentResults called", {
      instanceId,
      endpoint: "public/assessment/instance/" + instanceId + "/end",
      timestamp: new Date().toISOString(),
    });

    try {
      // throwHttpErrors:true so a non-2xx /end (after ky's retries) rejects
      // instead of being treated as success. A failed /end means the instance is
      // NOT submitted and would never be scored, so the caller MUST be able to
      // detect this and offer a retry rather than silently "finishing".
      const data = await api
        .post(
          "public/assessment/instance/" + instanceId + "/end",
          {
            headers: {
              "Content-Type": "application/json",
            },
            json: {},
            throwHttpErrors: true,
          },
        )
        .json();

      console.log("[SCORING-DIAG] submitFinalAssessmentResults SUCCESS", {
        instanceId,
        responseData: data,
        elapsedMs: Date.now() - startTime,
      });

      return data;
    } catch (error) {
      console.error("[SCORING-DIAG] submitFinalAssessmentResults FAILED", {
        instanceId,
        error: error?.message || error,
        errorName: error?.name,
        status: error?.response?.status,
        elapsedMs: Date.now() - startTime,
        timestamp: new Date().toISOString(),
      });
      if (typeof window !== "undefined" && window.TrackJS) {
        try {
          window.TrackJS.track(
            error instanceof Error
              ? error
              : new Error("submitFinalAssessmentResults failed: " + error),
          );
        } catch (_) {
          /* never let logging break the flow */
        }
      }
      // Re-throw so the caller surfaces a retry instead of advancing as if done.
      throw error;
    }
  }

  export async function callEvaluationService(definitionId, instanceId) {
    const startTime = Date.now();
    console.log("[SCORING-DIAG] callEvaluationService called", {
      definitionId,
      instanceId,
      endpoint: "https://scoring-api.sensay-learning.com/evaluate/" + definitionId + "/" + instanceId,
      timestamp: new Date().toISOString(),
    });

    try {
      const data = await apiOutside
        .get(
          "https://scoring-api.sensay-learning.com/evaluate/" + definitionId + "/" + instanceId,
        )
        .json();

      console.log("[SCORING-DIAG] callEvaluationService SUCCESS", {
        definitionId,
        instanceId,
        responseData: data,
        hasData: !!data,
        elapsedMs: Date.now() - startTime,
      });

      // Return the data so the caller can render the score (previously this was
      // dropped, leaving $finalScoreDetail undefined).
      return data;
    } catch (error) {
      console.error("[SCORING-DIAG] callEvaluationService FAILED", {
        definitionId,
        instanceId,
        error: error?.message || error,
        errorName: error?.name,
        elapsedMs: Date.now() - startTime,
        timestamp: new Date().toISOString(),
      });
      // Re-throw so the caller can decide (triggering scoring is best-effort —
      // the backend scheduler also re-scores SUBMITTED instances).
      throw error;
    }
  }

</script>
